<template>
    <div class="order-trend-chart">
        <div ref="chartContainer" class="chart-container"></div>
        <DashboardLoading :visible="initLoading && loading" text="加载订单走势数据..." size="normal" />
    </div>
</template>

<script>
import * as echarts from 'echarts'
import { getOrderTrend } from '@/api/dashboard'
import DashboardLoading from './DashboardLoading.vue'

export default {
  name: 'OrderTrendChart',
  components: {
    DashboardLoading
  },
  props: {
    fontSizes: {
      type: Object,
      default: () => ({
        title: 18,
        axis: 12,
        label: 10
      })
    }
  },
  data () {
    return {
      chart: null,
      resizeTimer: null,
      refreshTimer: null,
      chartData: [],
      loading: false,
      initLoading: true
    }
  },
  watch: {
    fontSizes: {
      handler () {
        this.updateChartOption()
      },
      deep: true
    }
  },
  mounted () {
    this.fetchOrderTrendData()
    window.addEventListener('resize', this.handleResize)
    // 每10分钟刷新一次数据
    this.refreshTimer = setInterval(() => {
      console.log('重新初始化订单走势数据', this.$moment(new Date(), 'YYYY-MM-DD HH:mm:ss'))
      this.fetchOrderTrendData()
    }, 600000)
  },
  beforeDestroy () {
    // 清理定时器
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
    // 销毁图表实例
    if (this.chart) {
      this.chart.dispose()
    }
    // 移除事件监听器
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async fetchOrderTrendData () {
      try {
        this.loading = true
        const response = await getOrderTrend()
        if (response.data && response.data.status && response.data.data) {
          const trendData = response.data.data.trend || []
          this.chartData = trendData.map(item => ({
            year: item.month,
            value: item.count
          }))
        } else {
          console.error('获取订单走势数据失败:', response)
          // 使用默认数据
          this.chartData = []
        }
      } catch (error) {
        console.error('获取订单走势数据异常:', error)
        // 使用默认数据
        this.chartData = []
      } finally {
        this.initLoading = false
        this.loading = false
        this.initChart()
      }
    },
    initChart () {
      // 销毁已有实例
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }

      // 使用 $nextTick 确保 DOM 完全渲染
      this.$nextTick(() => {
        // 添加延迟确保容器尺寸正确
        setTimeout(() => {
          this.createChart()
        }, 100)
      })
    },
    createChart () {
      const container = this.$refs.chartContainer
      if (!container) return

      // 检查容器是否有正确的尺寸
      const rect = container.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        // 如果容器尺寸为0，再次延迟初始化
        setTimeout(() => {
          this.createChart()
        }, 200)
        return
      }

      // 如果图表已存在，先销毁
      if (this.chart) {
        this.chart.dispose()
      }

      this.chart = echarts.init(container)
      this.setChartOption()
    },
    setChartOption () {
      const option = {
        title: {
          text: '',
          left: 'center',
          top: 0,
          textStyle: {
            color: '#ffffff',
            fontSize: this.fontSizes.title,
            fontWeight: 'bold'
          }
        },
        grid: {
          left: '5%',
          right: '5%',
          top: '5%',
          bottom: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.map(item => item.year),
          axisLine: {
            lineStyle: {
              color: '#4a90e2'
            }
          },
          axisLabel: {
            color: '#ffffff',
            fontSize: this.fontSizes.axis,
            rotate: 0
          },
          axisTick: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#ffffff',
            fontSize: this.fontSizes.axisLabelFontSize,
            formatter: function (value) {
              // 格式化Y轴标签，显示订单量
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M'
              } else if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K'
              } else {
                return value.toString()
              }
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(74, 144, 226, 0.3)',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            type: 'bar',
            data: this.chartData.map(item => ({
              value: item.value,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#4a90e2' },
                  { offset: 1, color: '#1e3a8a' }
                ])
              }
            })),
            barWidth: '60%',
            label: {
              show: true,
              position: 'top',
              color: '#ffffff',
              fontSize: this.fontSizes.axisLabelFontSize - 2,
              formatter: function (params) {
                const value = params.value
                if (value >= 1000000) {
                  return (value / 1000000).toFixed(1) + 'M'
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(0) + 'K'
                } else {
                  return value.toString()
                }
              }
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#60a5fa' },
                  { offset: 1, color: '#3b82f6' }
                ])
              }
            }
          }
        ],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 30, 80, 0.9)',
          borderColor: '#0096ff',
          borderWidth: 1,
          textStyle: {
            color: '#b8d4f0',
            fontSize: this.fontSizes.tooltipFontSize
          },
          formatter: (params) => {
            const data = params[0]
            let result = `<div style="padding: 8px;">`
            result += `<div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${data.name}</div>`
            result += `<div>订单量: <span style="color: #0096ff;">${data.value.toLocaleString()}单</span></div>`
            result += `</div>`
            return result
          },
          extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
        }
      }

      this.chart.setOption(option)
    },

    updateChartOption () {
      if (!this.chart || this.chart.isDisposed()) {
        this.initChart()
        return
      }

      const option = {
        title: {
          text: '',
          left: 'center',
          top: 0,
          textStyle: {
            color: '#ffffff',
            fontSize: this.fontSizes.title,
            fontWeight: 'bold'
          }
        },
        grid: {
          left: '5%',
          right: '5%',
          top: '5%',
          bottom: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.map(item => item.year),
          axisLine: {
            lineStyle: {
              color: '#4a90e2'
            }
          },
          axisLabel: {
            color: '#ffffff',
            fontSize: this.fontSizes.axis,
            rotate: 0
          },
          axisTick: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#ffffff',
            fontSize: this.fontSizes.axis,
            formatter: function (value) {
              // 格式化Y轴标签，显示订单量
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M'
              } else if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K'
              } else {
                return value.toString()
              }
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(74, 144, 226, 0.3)',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            type: 'bar',
            data: this.chartData.map(item => ({
              value: item.value,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#4a90e2' },
                  { offset: 1, color: '#1e3a8a' }
                ])
              }
            })),
            barWidth: '60%',
            label: {
              show: true,
              position: 'top',
              color: '#ffffff',
              fontSize: this.fontSizes.axisLabelFontSize - 3,
              formatter: function (params) {
                const value = params.value
                if (value >= 1000000) {
                  return (value / 1000000).toFixed(1) + 'M'
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(0) + 'K'
                } else {
                  return value.toString()
                }
              }
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#60a5fa' },
                  { offset: 1, color: '#3b82f6' }
                ])
              }
            }
          }
        ],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 30, 80, 0.9)',
          borderColor: '#0096ff',
          borderWidth: 1,
          textStyle: {
            color: '#b8d4f0',
            fontSize: this.fontSizes.tooltipFontSize
          },
          formatter: (params) => {
            const data = params[0]
            let result = `<div style="padding: 8px;">`
            result += `<div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${data.name}</div>`
            result += `<div>订单量: <span style="color: #0096ff;">${data.value.toLocaleString()}单</span></div>`
            result += `</div>`
            return result
          },
          extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
        }
      }

      this.chart.setOption(option, true)
    },

    handleResize () {
      // 防抖处理，避免频繁调用
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.chart && !this.chart.isDisposed()) {
          const container = this.$refs.chartContainer
          if (container) {
            const rect = container.getBoundingClientRect()
            // 只有在容器有正确尺寸时才调整图表大小
            if (rect.width > 0 && rect.height > 0) {
              try {
                this.chart.resize()
              } catch (error) {
                console.error('图表resize失败:', error)
                // 如果resize失败，尝试重新初始化
                this.initChart()
              }
            }
          }
        }
      }, 200) // 增加防抖延迟到200ms
    }
  }
}
</script>

<style lang="less" scoped>
.order-trend-chart {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.chart-container {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 0;
    /* 重要：允许flex子元素缩小 */
}
</style>
